import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

import Panel from "../../../../components/PartnerDashboard/Panel";
import Skeleton from "../../../../components/PartnerDashboard/Skeleton";
import {
  Empty,
  ErrorText,
  PageStack,
  Table,
  TableScroll,
  Td,
  Th,
  TwoCol,
} from "../../../../components/PartnerDashboard/ui";
import { adminGetUsageReport } from "../../../../api/apiEndpoints";
import { addDays } from "../../../../utils/reportDates";
import RangePicker, { useRange } from "../RangePicker";
import { Metric, MetricGrid, MetricLabel, MetricValue } from "../ui";

interface UsageReport {
  daily: { day: string; members: number; nonMembers: number; total: number }[];
  totals: { users: number; members: number; nonMembers: number; screenViews: number };
  sessions: { sessions: number; avgSeconds: number | null };
  firstTabs: { tab: string | null; isMember: boolean | null; count: number }[];
  screens: { screen: string | null; views: number; users: number }[];
  actions: { day: string; name: string; count: number; users: number }[];
}

const COLORS = { members: "#2a78d6", nonMembers: "#eb6834" };

const TAB_LABEL: Record<string, string> = {
  Dashboard: "Home (dashboard)",
  Explore: "Explore",
  Classes: "Classes",
  Profile: "Profile",
};

const SCREEN_LABEL: Record<string, string> = {
  Explore: "Explore",
  Dashboard: "Home (dashboard)",
  Profile: "Profile",
  Classes: "Classes",
  Settings: "Settings",
  CheckoutScreen: "Checkout",
  CheckoutSuccess: "Checkout success",
  ManageMembership: "Manage membership",
  MyJourney: "My Journey",
  PaymentBilling: "Payment & billing",
  ClassDropsHub: "Class Drops",
  ClassDropDetail: "Class Drop detail",
  ClassDropConfirm: "Class Drop confirm",
  ClassDropBooked: "Class Drop booked",
  OnboardingSurvey: "Onboarding survey",
  WelcomeOnboarding: "Onboarding intro",
  MoveGymConfirm: "Move gym confirm",
  DeleteAccount: "Delete account",
  Login: "Login",
  SignUpStep1: "Sign up (step 1)",
  SignUpStep2: "Sign up (step 2)",
};

const ACTION_LABEL: Record<string, string> = {
  checkin_code_shown: "Check-in code shown",
  class_booked: "Class booked",
  class_cancelled: "Class cancelled",
  class_drop_booked: "Class Drop booked",
  listing_viewed: "Gym/studio listing viewed",
  checkout_started: "Checkout started",
  checkout_completed: "Checkout completed",
  search_used: "Search used",
  map_opened: "Map opened",
  survey_completed: "Onboarding survey completed",
  rewards_opened: "Rewards opened",
  reward_claimed: "Reward claimed",
  trained_today_logged: "\"I trained today\" logged",
  inbox_opened: "Notifications opened",
  referral_shared: "Referral shared",
  price_filter_used: "Price filter used",
  classes_join_tapped: "Join/queue tapped from Classes",
  explore_credit_card_tapped: "$30 card tapped on Explore",
};

const TipBox = styled.div`
  background: #111111;
  color: #ffffff;
  font-family: "Aileron", "Poppins", sans-serif;
  font-size: 12px;
  padding: 8px 10px;
  border-radius: 8px;
  line-height: 1.6;
`;

const Swatch = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 6px;
  background: ${({ $color }) => $color};
`;

const dayLabel = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : "—");

const duration = (seconds: number | null) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s.toString().padStart(2, "0")}s`;
};

const UsagePage = () => {
  const range = useRange("last30");
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (range.invalid) return;
    let cancelled = false;
    setReport(null);
    setError(null);
    adminGetUsageReport({ start: range.range.start, end: range.endExclusive })
      .then((res) => !cancelled && setReport(res.data))
      .catch(() => !cancelled && setError("Couldn't load app usage. Please try again."));
    return () => {
      cancelled = true;
    };
  }, [range.range.start, range.endExclusive, range.invalid]);

  const series = useMemo(() => {
    if (range.invalid) return [];
    const byDay = new Map((report?.daily ?? []).map((d) => [d.day, d]));
    const out: { day: string; label: string; members: number; nonMembers: number; total: number }[] = [];
    for (let d = range.range.start; d <= range.range.end; d = addDays(d, 1)) {
      const row = byDay.get(d);
      out.push({
        day: d,
        label: dayLabel(d),
        members: row?.members ?? 0,
        nonMembers: row?.nonMembers ?? 0,
        total: row?.total ?? 0,
      });
    }
    return out;
  }, [report, range.range.start, range.range.end, range.invalid]);

  const avgDaily = series.length ? series.reduce((sum, d) => sum + d.total, 0) / series.length : 0;

  const firstTabs = useMemo(() => {
    const rows = report?.firstTabs ?? [];
    const tabs = Array.from(new Set(rows.map((r) => r.tab ?? "Unknown")));
    const total = (member: boolean) =>
      rows.filter((r) => !!r.isMember === member).reduce((sum, r) => sum + r.count, 0);
    const memberTotal = total(true);
    const otherTotal = total(false);
    return {
      memberTotal,
      otherTotal,
      rows: tabs
        .map((tab) => {
          const count = (member: boolean) =>
            rows.filter((r) => (r.tab ?? "Unknown") === tab && !!r.isMember === member).reduce((s, r) => s + r.count, 0);
          return { tab, members: count(true), others: count(false) };
        })
        .sort((a, b) => b.members + b.others - (a.members + a.others)),
    };
  }, [report]);

  const actions = useMemo(() => {
    const map = new Map<string, { name: string; count: number; days: number }>();
    (report?.actions ?? []).forEach((a) => {
      const row = map.get(a.name) ?? { name: a.name, count: 0, days: 0 };
      row.count += a.count;
      row.days += 1;
      map.set(a.name, row);
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [report]);

  const loading = !report && !error;
  const tiles = [
    { label: "People using the app", value: report?.totals.users },
    { label: "Members", value: report?.totals.members, color: COLORS.members },
    { label: "Non-members & guests", value: report?.totals.nonMembers, color: COLORS.nonMembers },
    { label: "Avg. daily active", value: report ? Math.round(avgDaily) : undefined },
    { label: "Sessions", value: report?.sessions.sessions },
    { label: "Avg. session length", text: report ? duration(report.sessions.avgSeconds) : undefined },
    { label: "Screen views", value: report?.totals.screenViews },
  ];

  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <TipBox>
        <div style={{ fontWeight: 700 }}>{p.label}</div>
        <div>
          <Swatch $color={COLORS.members} />
          Members: {p.members.toLocaleString()}
        </div>
        <div>
          <Swatch $color={COLORS.nonMembers} />
          Non-members & guests: {p.nonMembers.toLocaleString()}
        </div>
        <div>Total: {p.total.toLocaleString()}</div>
      </TipBox>
    );
  };

  return (
    <PageStack>
      <Panel
        title="Period"
        info="Counts come from the mobile app's usage tracking, which only covers app versions that include it. Days follow Eastern time."
        subtitle={`${range.label}${range.label ? " · Eastern time" : ""}`}
      >
        <RangePicker state={range} />
        {error && <ErrorText>{error}</ErrorText>}
      </Panel>

      {!error && (
        <>
          <MetricGrid>
            {tiles.map((t) => (
              <Metric key={t.label}>
                {loading ? (
                  <Skeleton $w="50%" $h="24px" $radius="8px" />
                ) : (
                  <MetricValue>{t.text ?? (t.value != null ? t.value.toLocaleString() : "—")}</MetricValue>
                )}
                <MetricLabel>
                  {t.color && <Swatch $color={t.color} />}
                  {t.label}
                </MetricLabel>
              </Metric>
            ))}
          </MetricGrid>

          <Panel
            title="Daily active people"
            info="Signed-in people are counted once per day by account; guests are counted by device. Someone whose membership changes mid-day can appear in both groups."
          >
            {loading ? (
              <Skeleton $h="280px" $radius="12px" />
            ) : (report?.totals.users ?? 0) === 0 ? (
              <Empty>No app activity recorded in this period yet.</Empty>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={series} margin={{ top: 6, right: 6, left: 0, bottom: 0 }} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe4" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#9a8e84" }}
                    axisLine={{ stroke: "#e7e0d8" }}
                    tickLine={false}
                    minTickGap={12}
                  />
                  <YAxis width={36} tick={{ fontSize: 11, fill: "#9a8e84" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={renderTooltip} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="square"
                    iconSize={9}
                    wrapperStyle={{ fontSize: 12, color: "#4a423b", paddingBottom: 8 }}
                    formatter={(value: string) => (
                      <span style={{ color: "#4a423b" }}>{value === "members" ? "Members" : "Non-members & guests"}</span>
                    )}
                  />
                  <Bar dataKey="members" stackId="u" fill={COLORS.members} stroke="#ffffff" strokeWidth={1} />
                  <Bar dataKey="nonMembers" stackId="u" fill={COLORS.nonMembers} stroke="#ffffff" strokeWidth={1} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <TwoCol>
            <Panel
              title="First tab tapped after opening"
              info="The first bottom-tab tap in each app session. Sessions where nobody taps a tab (they stay on the screen the app opened to) aren't counted."
            >
              {loading ? (
                <Skeleton $h="180px" $radius="12px" />
              ) : firstTabs.rows.length === 0 ? (
                <Empty>No tab taps recorded yet.</Empty>
              ) : (
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Tab</Th>
                        <Th $align="right">Members</Th>
                        <Th $align="right">Non-members</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {firstTabs.rows.map((r) => (
                        <tr key={r.tab}>
                          <Td>{TAB_LABEL[r.tab] ?? r.tab}</Td>
                          <Td $align="right">
                            {pct(r.members, firstTabs.memberTotal)} <span style={{ color: "#9a8e84" }}>({r.members})</span>
                          </Td>
                          <Td $align="right">
                            {pct(r.others, firstTabs.otherTotal)} <span style={{ color: "#9a8e84" }}>({r.others})</span>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
              )}
            </Panel>

            <Panel title="Most-viewed screens" info="Full screens only. Pop-up sheets like Rewards or a gym listing are counted under key actions.">
              {loading ? (
                <Skeleton $h="180px" $radius="12px" />
              ) : (report?.screens.length ?? 0) === 0 ? (
                <Empty>No screen views recorded yet.</Empty>
              ) : (
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Screen</Th>
                        <Th $align="right">Views</Th>
                        <Th $align="right">People</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {report!.screens.map((s) => (
                        <tr key={s.screen ?? "unknown"}>
                          <Td>{SCREEN_LABEL[s.screen ?? ""] ?? s.screen ?? "Unknown"}</Td>
                          <Td $align="right">{s.views.toLocaleString()}</Td>
                          <Td $align="right">{s.users.toLocaleString()}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
              )}
            </Panel>
          </TwoCol>

          <Panel title="Key actions" info="How often each action happened in the period, and on how many days it happened at least once.">
            {loading ? (
              <Skeleton $h="180px" $radius="12px" />
            ) : actions.length === 0 ? (
              <Empty>No key actions recorded yet.</Empty>
            ) : (
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <Th>Action</Th>
                      <Th $align="right">Times</Th>
                      <Th $align="right">Days with activity</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {actions.map((a) => (
                      <tr key={a.name}>
                        <Td>{ACTION_LABEL[a.name] ?? a.name}</Td>
                        <Td $align="right">{a.count.toLocaleString()}</Td>
                        <Td $align="right">{a.days.toLocaleString()}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            )}
          </Panel>
        </>
      )}
    </PageStack>
  );
};

export default UsagePage;
